import type { Enums, Tables } from '../../db_types'

export type PayoutStatus = Enums<'payout_status'>
export type PayoutInstallmentStatus = Enums<'payout_installment_status'>

export type PayoutDisplayStatus =
  | 'informative'
  | 'expired'
  | 'locked'
  | 'pending_validation'
  | 'ongoing'
  | 'pending_payment'
  | 'paid'
  | 'validated'

export type PayoutStatusInstallment = Pick<
  Tables<'payout_installments'>,
  'status' | 'statement_id'
> & { id?: number }
export const isPayoutInstallmentPaid = (
  installment?: Pick<Tables<'payout_installments'>, 'status'> | null
) => installment?.status === 'paid'

export const isPayoutInstallmentExpired = (
  installment?: Pick<Tables<'payout_installments'>, 'status'> | null
) => installment?.status === 'expired'

export type PayoutStatusLike = Pick<
  Tables<'payouts'>,
  | 'status'
  | 'plan_id'
  | 'compensation_rule_id'
  | 'target_id'
  | 'period_end'
  | 'is_withheld'
  | 'pending_validation'
  | 'statement_id'
> & {
  payout_installments?: PayoutStatusInstallment[] | null
  current_payout_installment?: PayoutStatusInstallment | null
}

export type PayoutDisplayStatusOptions = {
  hasValidationFlow?: boolean
  informative?: boolean
  now?: Date
}

export type PayoutAssignmentLike = Pick<Tables<'payouts'>, 'statement_id'> & {
  payout_installments?:
    | (
        | (Pick<Tables<'payout_installments'>, 'statement_id'> & {
            status?: PayoutInstallmentStatus | null
          })
        | null
      )[]
    | null
}

export const isPayoutStatementUnassigned = (payout: PayoutAssignmentLike) => {
  const hasUnassignedInstallment = payout.payout_installments?.some(
    installment => installment?.status !== 'expired' && installment?.statement_id == null
  )

  return (
    hasUnassignedInstallment === true ||
    (payout.statement_id == null &&
      !payout.payout_installments?.some(
        installment => installment?.status !== 'expired' && installment?.statement_id != null
      ))
  )
}

export const isPayoutPaid = (payout?: Pick<Tables<'payouts'>, 'status'> | null) =>
  payout?.status === 'paid'

export const isPayoutValidated = (payout?: Pick<Tables<'payouts'>, 'status'> | null) =>
  payout?.status === 'validated' || isPayoutPaid(payout)

type PayoutExpiryLike = Pick<Tables<'payouts'>, 'status'> & {
  payout_installments?: (PayoutStatusInstallment | null)[] | null
  current_payout_installment?: PayoutStatusInstallment | null
}

export const isPayoutExpired = (
  payout?: PayoutExpiryLike,
  { includeInstallments = true }: { includeInstallments?: boolean } = {}
) => {
  if (!payout) return false

  const installments = payout.payout_installments
  const hasOnlyExpiredInstallments =
    includeInstallments &&
    installments != null &&
    installments.length > 0 &&
    installments.every(installment => installment?.status === 'expired')

  return (
    payout.status === 'expired' ||
    (includeInstallments && payout.current_payout_installment?.status === 'expired') ||
    hasOnlyExpiredInstallments
  )
}

export const isPayoutPendingValidation = (
  payout?: Pick<Tables<'payouts'>, 'status' | 'pending_validation'> | null
) => payout?.status === 'achieved' && payout.pending_validation === true

export const isPayoutOngoing = (
  payout?: Pick<
    Tables<'payouts'>,
    | 'status'
    | 'plan_id'
    | 'compensation_rule_id'
    | 'target_id'
    | 'is_withheld'
    | 'pending_validation'
    | 'period_end'
  > | null,
  now = new Date()
) =>
  !!payout &&
  payout.status === 'achieved' &&
  (payout.plan_id != null || payout.compensation_rule_id != null) &&
  payout.target_id == null &&
  payout.is_withheld !== true &&
  !isPayoutPendingValidation(payout) &&
  payout.period_end != null &&
  new Date(payout.period_end) >= now

export const getPayoutDisplayStatus = (
  payout: PayoutStatusLike,
  {
    hasValidationFlow = true,
    informative = false,
    now = new Date()
  }: PayoutDisplayStatusOptions = {}
): PayoutDisplayStatus => {
  const firstInstallment = [...(payout.payout_installments ?? [])].sort(
    (left, right) => (left.id ?? Number.POSITIVE_INFINITY) - (right.id ?? Number.POSITIVE_INFINITY)
  )[0]
  const activeInstallment = payout.current_payout_installment ?? firstInstallment ?? null

  if (informative) {
    return 'informative'
  }

  if (isPayoutExpired(payout) || activeInstallment?.status === 'expired') {
    return 'expired'
  }

  if (isPayoutStatementUnassigned(payout)) {
    return 'locked'
  }

  if (hasValidationFlow && isPayoutPendingValidation(payout)) {
    return 'pending_validation'
  }

  if (isPayoutOngoing(payout, now)) {
    return 'ongoing'
  }

  if (
    payout.status === 'achieved' &&
    (!activeInstallment || activeInstallment.status === 'pending')
  ) {
    return 'pending_payment'
  }

  if (activeInstallment?.status === 'paid' || payout.status === 'paid') {
    return 'paid'
  }

  if (payout.status === 'validated') {
    return 'validated'
  }

  return 'pending_payment'
}
